import type {AxiosError} from "axios";

import {
  isShiftDomainErrorCode,
  resolveErrorCode,
  SHIFT_BREAK_GLITCHTIP_CAPTURE_ALLOWLIST_4XX,
} from "./error-codes";
import {isShiftOrBreakHttpPath, normalizeHttpPathTag} from "./http-context";

function isAxiosError(error: unknown): error is AxiosError<{error_code?: string}> {
  return Boolean(error && typeof error === "object" && "isAxiosError" in error);
}

function isAllowlistedShiftBreak4xx(error: AxiosError<{error_code?: string}>): boolean {
  const code = error.response?.data?.error_code;
  return typeof code === "string" && SHIFT_BREAK_GLITCHTIP_CAPTURE_ALLOWLIST_4XX.has(code);
}

/** Realtime config/auth failures use domain capture (`realtime_connection_failed`), not axios. */
function isRealtimeAxiosSkipPath(url?: string): boolean {
  const path = normalizeHttpPathTag(url);
  return path === "broadcasting/config" || path.startsWith("broadcasting/");
}

/** Help content is prefetched with local cache fallback — network failures are non-actionable. */
function isHelpPrefetchPath(url?: string): boolean {
  const path = normalizeHttpPathTag(url);
  return path === "help" || path.startsWith("help/");
}

/** Auth/session and expected business 4xx are skipped; 5xx and network are captured. */
export function shouldCaptureAxiosError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;

  if (status === 401 || status === 403) return false;
  if (status === 404 || status === 422) return false;

  if (!error.response && error.request) {
    if (isRealtimeAxiosSkipPath(error.config?.url)) return false;
    if (isHelpPrefetchPath(error.config?.url)) return false;
    return true;
  }
  if (typeof status === "number" && status >= 500) {
    if (isHelpPrefetchPath(error.config?.url)) return false;
    return true;
  }

  if (typeof status === "number" && status >= 400 && status < 500) {
    if (isShiftOrBreakHttpPath(error.config?.url)) {
      if (status === 400 && isAllowlistedShiftBreak4xx(error)) return true;
      return false;
    }

    const resolvedCode = resolveErrorCode(error);
    if (isShiftDomainErrorCode(resolvedCode)) return false;

    return false;
  }

  return false;
}

export function resolveAxiosCaptureLevel(error: unknown): "warning" | "error" {
  if (!isAxiosError(error)) return "error";

  const status = error.response?.status;
  if (!error.response && error.request) return "error";
  if (typeof status === "number" && status >= 500) return "error";
  if (typeof status === "number" && status >= 400) return "warning";

  return "error";
}
